import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Button } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { RelatorioMedicoBase } from '@/features/registro/medicos/relatorios/relatorio-medico.base';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { CardMedicoSelecionado } from '@/features/registro/medicos/relatorios/components/card-medico-selecionado/card-medico-selecionado';
import { CertidaoCadastralMedicoService } from '@/core/api/sicom-api/registro/medico/relatorios/certidao-cadastral-medico/certidao-cadastral-medico.service';
import { ReportCertidaoCadastralMedicoRequest, ReportCertidaoCadastralMedicoResponse } from '@/core/api/sicom-api/registro/medico/relatorios/certidao-cadastral-medico/certidao-cadastral-medico.model';
import { CertidaoCadastralMedicoJobService } from '@/core/services/certidao-cadastral-medico-job.service';
import { CanDirective } from '@/core/permissions/can.directive';

type Req = ReportCertidaoCadastralMedicoRequest;
type Resp = ReportCertidaoCadastralMedicoResponse;

@Component({
    selector: 'app-certidao-cadastral-medico',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, TooltipModule, RelatorioContainer, Pesquisa, CardMedicoSelecionado, CanDirective],
    templateUrl: './certidao-cadastral-medico.html',
    styleUrl: './certidao-cadastral-medico.scss'
})
/**
 * Relatório PDF assíncrono da Certidão Cadastral do médico (nuCrm/sgUf).
 * Herda `RelatorioMedicoBase`: toda a orquestração de export/polling/download/erro e o card
 * "médico selecionado + Alterar" vêm da base; aqui só delegamos o HTTP ao service.
 */
export class ReportCertidaoCadastralMedicoComponent extends RelatorioMedicoBase<Req, Resp> {
    private readonly certidaoService = inject(CertidaoCadastralMedicoService);
    readonly certidaoJobService = inject(CertidaoCadastralMedicoJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.certidaoJobService.job$;

    protected get jobFacade(): RelatorioJobFacade {
        return this.certidaoJobService;
    }

    /** Monta o request a partir do médico selecionado; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        return this.resolverIdentificacaoMedico('Selecione um médico (CRM/UF) antes de gerar a certidão.');
    }

    protected exportar(request: Req) {
        return this.certidaoService.exportarPdf(request);
    }

    /** Limpa o job/preview corrente (a certidão não tem formulário de dados a resetar). */
    limparFormulario(): void {
        this.limparJob();
    }
}
